import { useCallback, useMemo } from 'react';

import { useNavigate, useSearch } from '@tanstack/react-router';

export interface HistorySearchParams {
  q: string;
  direction: 'asc' | 'desc';
}

export const HISTORY_SEARCH_DEFAULTS: HistorySearchParams = {
  q: '',
  direction: 'desc',
};

const VALID_DIRECTIONS: ReadonlySet<string> = new Set(['asc', 'desc']);

export function parseHistorySearch(raw: Record<string, unknown>): HistorySearchParams {
  return {
    q: typeof raw.q === 'string' ? raw.q : HISTORY_SEARCH_DEFAULTS.q,
    direction: VALID_DIRECTIONS.has(raw.direction as string)
      ? (raw.direction as 'asc' | 'desc')
      : HISTORY_SEARCH_DEFAULTS.direction,
  };
}

export interface HistoryRouterSearch {
  search: HistorySearchParams;
  navigate: (updates: Partial<HistorySearchParams>) => void;
  setFieldFilter: (fieldKey: string) => void;
  setDirection: (direction: HistorySearchParams['direction']) => void;
}

type NavigateFn = (opts: {
  search: (prev: HistorySearchParams) => HistorySearchParams;
  replace?: boolean;
}) => void;

// TanStack types `from` as a route-id literal; this module is route-agnostic,
// so the casts are contained here.
export function createHistoryRouterSearch(routePath: string) {
  return function useHistoryRouterSearch(): HistoryRouterSearch {
    const search = useSearch({ from: routePath } as never) as HistorySearchParams;
    const tanNavigate = useNavigate({ from: routePath } as never) as unknown as NavigateFn;

    const navigate = useCallback(
      (updates: Partial<HistorySearchParams>) => {
        tanNavigate({ search: (prev) => ({ ...prev, ...updates }), replace: true });
      },
      [tanNavigate],
    );

    return useMemo(
      () => ({
        search,
        navigate,
        setFieldFilter: (fieldKey: string) => navigate({ q: fieldKey }),
        setDirection: (direction: HistorySearchParams['direction']) => navigate({ direction }),
      }),
      [search, navigate],
    );
  };
}
